"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export function TrackLookupForm({ userEmail }: { userEmail?: string }) {
  const router = useRouter();
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState(userEmail ?? "");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) return;
    setLoading(true);
    router.push(
      `/track/${encodeURIComponent(orderId.trim())}?email=${encodeURIComponent(email.trim())}`
    );
  };

  return (
    <div className="bg-background min-h-screen py-20">
      <div className="container mx-auto max-w-4xl px-4">
        <Link
          href="/"
          className="text-muted-foreground hover:text-foreground mb-6 inline-block text-xs font-bold tracking-widest uppercase"
        >
          ← Back to store
        </Link>
        <h1 className="mb-2 text-2xl font-black tracking-tighter uppercase md:text-4xl">
          Track Your Order
        </h1>
        <p className="text-muted-foreground mb-8 text-sm md:text-base">
          Enter your order ID and the email address used at checkout to view your order status.
        </p>

        <form onSubmit={handleSubmit} className="border-border space-y-4 border p-6">
          <label htmlFor="track-order-id" className="flex flex-col gap-2 text-[10px] font-bold tracking-widest uppercase">
            Order ID
            <Input
              id="track-order-id"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="e.g. 071E2C or full order ID"
              required
              className="h-12 rounded-none font-mono normal-case"
            />
          </label>

          <label htmlFor="track-email" className="flex flex-col gap-2 text-[10px] font-bold tracking-widest uppercase">
            Email address
            <Input
              id="track-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="h-12 rounded-none normal-case"
            />
          </label>

          <Button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-none text-xs font-bold tracking-widest uppercase"
          >
            {loading ? (
              "Loading…"
            ) : (
              <>
                <Search className="h-4 w-4" />
                Track Order
              </>
            )}
          </Button>
        </form>

        <p className="text-muted-foreground mt-6 text-center text-xs">
          You can find your order ID in your confirmation email or your account dashboard.
        </p>
      </div>
    </div>
  );
}
